import { ReactElement, ReactNode, useState } from "react";
import { MoreVert as MoreVertIcon } from "@remo-co/ui-core/src/icons/MoreVert";
import classNames from "classnames";
import { useI18n } from "i18n";
import { ActionButton } from "modules/actionButton";
import { useTheme } from "@remo-co/ui-core/src/hooks/useTheme";
import { SizeType } from "@remo-co/ui-core/src/types";
import { useMediaQuery } from "@remo-co/ui-core/src/hooks/useMediaQuery";
import { useStyles } from "./styles";

type LayoutOption =
  | "hide-on-mobile"
  | "mobile-only"
  | "desktop-only"
  | "desktop-and-mobile";

interface Layout {
  layout: LayoutOption;
  component: ReactNode;
  className?: string;
  id: string;
}

const ExpandableContainer = ({
  components,
  desktopContainerClassName,
  mobileContainerClassName,
  breakpoint = "lg",
}: {
  components: Layout[];
  desktopContainerClassName?: string;
  mobileContainerClassName?: string;
  breakpoint?: SizeType;
}): ReactElement => {
  const { t } = useI18n();
  const [opened, setIsOpened] = useState<boolean>(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up(breakpoint));

  const styles = useStyles({ breakpoint, opened });

  return (
    <div
      className={classNames(styles.desktopContainer, desktopContainerClassName)}
    >
      <div
        data-testid="mobile-container"
        className={classNames(styles.mobileContainer, mobileContainerClassName)}
      >
        {!isDesktop &&
          components
            .filter((item) =>
              ["hide-on-mobile", "mobile-only"].includes(item.layout),
            )
            .map((item) => (
              <div className={item.className} key={item.id}>
                {item.component}
              </div>
            ))}
      </div>
      <div data-testid="desktop-container" className={styles.childrenContainer}>
        {components
          .filter((item) => {
            if (!isDesktop) {
              return ["desktop-and-mobile"].includes(item.layout);
            }
            return [
              "desktop-only",
              "desktop-and-mobile",
              "hide-on-mobile",
            ].includes(item.layout);
          })
          .map((item) => (
            <div className={item.className} key={item.id}>
              {item.component}
            </div>
          ))}
        {!isDesktop && (
          <ActionButton
            data-testid="expand-button"
            onClick={() => setIsOpened(!opened)}
            label={t("more")}
            isActive={opened}
            icon={<MoreVertIcon />}
            darkMode
          />
        )}
      </div>
    </div>
  );
};

export default ExpandableContainer;
